import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { 
  Colors, 
  Spacing, 
  FontSizes, 
  FontWeights, 
  BorderRadius, 
  Shadows,
  FontStacks 
} from '../../constants/theme';
import { 
  ChevronUpIcon, 
  ChevronDownIcon,
  ArrowUpIcon,
  ArrowDownIcon 
} from '../icons/CorporateIcons';

// Styled Components
const TableContainer = styled.div`
  width: 100%;
  overflow: hidden;
  background: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.sm};
  border: 1px solid ${Colors.borderLight};
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  
  /* Modern scrollbar */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${Colors.background};
    border-radius: ${BorderRadius.sm}px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${Colors.border};
    border-radius: ${BorderRadius.sm}px;
    
    &:hover {
      background: ${Colors.textTertiary};
    }
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-family: ${FontStacks.primary};
`;

const TableHead = styled.thead`
  background: linear-gradient(to bottom, ${Colors.background}, ${Colors.backgroundDark});
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TableHeaderRow = styled.tr`
  border-bottom: 2px solid ${Colors.border};
`;

const TableHeaderCell = styled.th<{ 
  sortable?: boolean; 
  align?: 'left' | 'center' | 'right';
  width?: string;
}>`
  padding: ${Spacing.lg}px ${Spacing.xl}px;
  text-align: ${props => props.align || 'left'};
  color: ${Colors.textSecondary};
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border-bottom: 2px solid ${Colors.border};
  background: ${Colors.background};
  white-space: nowrap;
  position: relative;
  ${props => props.width && `width: ${props.width};`}
  
  ${props => props.sortable && `
    cursor: pointer;
    user-select: none;
    transition: all 0.2s ease;
    
    &:hover {
      color: ${Colors.textPrimary};
      background: ${Colors.surfaceHover};
    }
  `}
`;

const SortIndicator = styled.span<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  margin-left: ${Spacing.xs}px;
  color: ${props => props.active ? Colors.primary : Colors.textTertiary};
  transition: color 0.2s ease;
`;

const TableBody = styled.tbody`
  background: ${Colors.surface};
`;

const TableRow = styled.tr<{ 
  clickable?: boolean; 
  selected?: boolean;
  highlighted?: boolean;
}>`
  transition: all 0.2s ease;
  border-bottom: 1px solid ${Colors.borderLight};
  
  ${props => props.selected && `
    background: ${Colors.primary}10;
    border-left: 3px solid ${Colors.primary};
  `}
  
  ${props => props.highlighted && `
    background: ${Colors.warning}10;
  `}
  
  ${props => props.clickable && `
    cursor: pointer;
    
    &:hover {
      background: ${Colors.surfaceHover};
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      transform: translateX(2px);
    }
  `}
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td<{ 
  align?: 'left' | 'center' | 'right';
  bold?: boolean;
  color?: string;
  nowrap?: boolean;
}>`
  padding: ${Spacing.lg}px ${Spacing.xl}px;
  text-align: ${props => props.align || 'left'};
  color: ${props => props.color || Colors.textPrimary};
  font-size: ${FontSizes.sm}px;
  font-weight: ${props => props.bold ? FontWeights.semibold : FontWeights.normal};
  line-height: 1.5;
  ${props => props.nowrap && 'white-space: nowrap;'}
`;

const Badge = styled.span<{ 
  variant?: 'success' | 'warning' | 'danger' | 'info' | 'default' 
}>`
  display: inline-flex;
  align-items: center;
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  border-radius: ${BorderRadius.round}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
  line-height: 1;
  white-space: nowrap;
  
  ${props => {
    const variants = {
      success: { bg: Colors.success + '20', color: Colors.success },
      warning: { bg: Colors.warning + '20', color: Colors.warning },
      danger: { bg: Colors.danger + '20', color: Colors.danger },
      info: { bg: Colors.info + '20', color: Colors.info },
      default: { bg: Colors.borderLight, color: Colors.textSecondary }
    };
    const style = variants[props.variant || 'default'];
    return `
      background: ${style.bg};
      color: ${style.color};
    `;
  }}
`;

const ProgressBar = styled.div<{ value: number; color?: string }>`
  width: 100%;
  height: 6px;
  background: ${Colors.borderLight};
  border-radius: ${BorderRadius.round}px;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${props => props.value}%;
    background: ${props => props.color || Colors.primary};
    border-radius: ${BorderRadius.round}px;
    transition: width 0.3s ease;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${Spacing.xxxl}px;
  color: ${Colors.textTertiary};
  font-size: ${FontSizes.md}px;
`;

const TableFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.lg}px ${Spacing.xl}px;
  background: ${Colors.background};
  border-top: 1px solid ${Colors.border};
`;

const PaginationInfo = styled.div`
  color: ${Colors.textSecondary};
  font-size: ${FontSizes.sm}px;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: ${Spacing.sm}px;
`;

const PaginationButton = styled.button<{ active?: boolean; disabled?: boolean }>`
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background: ${props => props.active ? Colors.primary : Colors.surface};
  color: ${props => props.active ? Colors.textOnPrimary : Colors.textPrimary};
  border: 1px solid ${props => props.active ? Colors.primary : Colors.border};
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? Colors.primaryDark : Colors.surfaceHover};
    border-color: ${Colors.primary};
  }
`;

// Types
export interface Column<T = any> {
  key: string;
  header: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  render?: (value: any, row: T, index: number) => React.ReactNode;
}

export interface ModernTableProps<T = any> {
  columns: Column<T>[];
  data: T[];
  keyField?: string;
  onRowClick?: (row: T, index: number) => void;
  selectedRow?: T;
  highlightedRows?: T[];
  emptyMessage?: string;
  showPagination?: boolean;
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  className?: string;
}

// Helper components for common cell renders
export const CellHelpers = {
  Badge: ({ value, variant }: { value: string; variant?: 'success' | 'warning' | 'danger' | 'info' }) => (
    <Badge variant={variant}>{value}</Badge>
  ),
  
  Progress: ({ value, color, label }: { value: number; color?: string; label?: string }) => (
    <div style={{ width: '100%' }}>
      {label && <div style={{ fontSize: FontSizes.xs, color: Colors.textSecondary, marginBottom: Spacing.xs }}>{label}</div>}
      <ProgressBar value={value} color={color} />
    </div>
  ),
  
  Currency: ({ value, currency = 'USD' }: { value: number; currency?: string }) => (
    <span style={{ fontWeight: FontWeights.semibold }}>
      {new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value)}
    </span>
  ),
  
  Percentage: ({ value, color }: { value: number; color?: string }) => {
    const displayColor = color || (value >= 0 ? Colors.success : Colors.danger);
    return (
      <span style={{ color: displayColor, fontWeight: FontWeights.semibold }}>
        {value >= 0 ? '+' : ''}{value.toFixed(1)}%
      </span>
    );
  },
  
  Date: ({ value }: { value: string | Date }) => {
    const date = value instanceof Date ? value : new Date(value);
    return <span>{date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>;
  },
  
  Avatar: ({ name, image }: { name: string; image?: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: Spacing.sm }}>
      <div style={{
        width: 32,
        height: 32,
        borderRadius: BorderRadius.round,
        background: image ? `url(${image})` : Colors.primary,
        backgroundSize: 'cover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: Colors.textOnPrimary,
        fontSize: FontSizes.sm,
        fontWeight: FontWeights.semibold
      }}>
        {!image && name.charAt(0).toUpperCase()}
      </div>
      <span>{name}</span>
    </div>
  )
};

function ModernTable<T = any>({
  columns,
  data,
  keyField = 'id',
  onRowClick,
  selectedRow,
  highlightedRows = [],
  emptyMessage = 'No data available',
  showPagination = false,
  pageSize = 10,
  currentPage = 1,
  onPageChange,
  className
}: ModernTableProps<T>) {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Handle sorting
  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortColumn) return data;

    return [...data].sort((a, b) => {
      const aValue = (a as any)[sortColumn];
      const bValue = (b as any)[sortColumn];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number') {
        return sortDirection === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }

      return 0;
    });
  }, [data, sortColumn, sortDirection]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!showPagination) return sortedData;
    
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return sortedData.slice(start, end);
  }, [sortedData, showPagination, currentPage, pageSize]);

  const totalPages = Math.ceil(data.length / pageSize);

  const isRowHighlighted = (row: T) => {
    return highlightedRows.some(hr => (hr as any)[keyField] === (row as any)[keyField]);
  };

  const isRowSelected = (row: T) => {
    return selectedRow && (selectedRow as any)[keyField] === (row as any)[keyField];
  };

  if (data.length === 0) {
    return (
      <TableContainer className={className}>
        <EmptyState>{emptyMessage}</EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer className={className}>
      <TableWrapper>
        <StyledTable>
          <TableHead>
            <TableHeaderRow>
              {columns.map(column => (
                <TableHeaderCell
                  key={column.key}
                  sortable={column.sortable}
                  align={column.align}
                  width={column.width}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  {column.header}
                  {column.sortable && (
                    <SortIndicator active={sortColumn === column.key}>
                      {sortColumn === column.key ? (
                        sortDirection === 'asc' ? <ArrowUpIcon size={12} /> : <ArrowDownIcon size={12} />
                      ) : (
                        <ChevronUpIcon size={10} />
                      )}
                    </SortIndicator>
                  )}
                </TableHeaderCell>
              ))}
            </TableHeaderRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, rowIndex) => (
              <TableRow
                key={(row as any)[keyField] || rowIndex}
                clickable={!!onRowClick}
                selected={isRowSelected(row)}
                highlighted={isRowHighlighted(row)}
                onClick={() => onRowClick && onRowClick(row, rowIndex)}
              >
                {columns.map(column => (
                  <TableCell
                    key={column.key}
                    align={column.align}
                  >
                    {column.render 
                      ? column.render((row as any)[column.key], row, rowIndex)
                      : (row as any)[column.key]
                    }
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableWrapper>
      
      {showPagination && totalPages > 1 && (
        <TableFooter>
          <PaginationInfo>
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, data.length)} of {data.length} entries
          </PaginationInfo>
          <PaginationControls>
            <PaginationButton
              disabled={currentPage === 1}
              onClick={() => onPageChange && onPageChange(currentPage - 1)}
            >
              Previous
            </PaginationButton>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              const pageNum = i + 1;
              return (
                <PaginationButton
                  key={pageNum}
                  active={currentPage === pageNum}
                  onClick={() => onPageChange && onPageChange(pageNum)}
                >
                  {pageNum}
                </PaginationButton>
              );
            })}
            {totalPages > 5 && <span style={{ padding: `0 ${Spacing.sm}px` }}>...</span>}
            {totalPages > 5 && (
              <PaginationButton
                active={currentPage === totalPages}
                onClick={() => onPageChange && onPageChange(totalPages)}
              >
                {totalPages}
              </PaginationButton>
            )}
            <PaginationButton
              disabled={currentPage === totalPages}
              onClick={() => onPageChange && onPageChange(currentPage + 1)}
            >
              Next
            </PaginationButton>
          </PaginationControls>
        </TableFooter>
      )}
    </TableContainer>
  );
}

export default ModernTable;